
import { api } from "core/axios/axios";
import type {
  ICustomer,
  IPaginated,
  IListCustomersParams,
  ICustomerGroup,
  IListCustomerGroupsParams,
  IPaginatedGroups,
} from "./types";

/** Map raw item → ICustomer (adds `name`) */
function adaptCustomer(raw: any): ICustomer {
  const first = raw?.firstName ?? null;
  const last = raw?.lastName ?? null;
  const name = [first, last].filter(Boolean).join(" ").trim();

  return {
    id: Number(raw?.id),
    firstName: first,
    lastName: last,
    name,
    phoneNumber: raw?.phoneNumber ?? null,
    whatsappNumber: raw?.whatsappNumber ?? null,
    messengerId: raw?.messengerId ?? null,
    email: raw?.email ?? null,
    groupId: raw?.groupId ?? null,
    group: raw?.group,
    status: raw?.status ?? "",
  };
}

/** Shape your backend returns (for clarity) */
type CustomersResponseRaw = {
  customers: any[];
  totalPages: number;
  totalCount: number;
  page: number;
  pageSize: number;
};

/** Fetch & normalize into IPaginated<ICustomer> */
export async function getCustomers(
  params: IListCustomersParams = {}
): Promise<IPaginated<ICustomer>> {
  const { page = 1, pageSize = 10, groupId, search } = params;

  const { data } = await api.get<CustomersResponseRaw>("customers", {
    params: { page, pageSize, groupId, ...(search ? { search } : {}) },
  });

  const items = Array.isArray(data?.customers) ? data.customers : [];
  const adapted = items.map(adaptCustomer);

  return {
    data: adapted,
    page: Number(data?.page ?? page),
    limit: Number(data?.pageSize ?? pageSize),
    total: Number(data?.totalCount ?? adapted.length),
    totalPages: Number(data?.totalPages ?? 1),
  };
}




/*************  DELETE FUNCTIONALITY     *****************/
export async function deleteCustomer(id: number | string): Promise<void> {
  await api.delete(`customers/${id}`);
}

export async function deleteCustomerGroups(id: number | string): Promise<void> {
  return api.delete("customergroup/" + id);
}


/* Leads Group */
function adaptGroup(raw: any): ICustomerGroup {
  return {
    id: Number(raw?.id),
    name: String(raw?.name ?? ""),
    status: raw?.status ?? undefined,
    organizationId: raw?.organizationId ?? undefined,
    createdUser: raw?.createdUser ?? undefined,
    uuid: raw?.uuid ?? undefined,
    leadsCount: raw?.leadsCount ?? undefined,
  };
}

export async function getCustomerGroupsRes(): Promise<ICustomerGroup[]> {
  const { data } = await api.get("customergroup");
  const arr = Array.isArray(data) ? data : [];
  return arr.map(adaptGroup);
}

export async function getCustomerGroupsPaginated(
  params: IListCustomerGroupsParams = {}
): Promise<IPaginatedGroups> {
  const { search, page, pageSize, status } = params;

  const query: Record<string, any> = {};
  if (page !== undefined) query.page = page;
  if (pageSize !== undefined) query.pageSize = pageSize;
  if (search) query.search = search;
  if (status && status !== "all") query.status = status;

  const { data } = await api.get("customergroup", { params: query });

  if (Array.isArray(data)) {
    const items = data.map(adaptGroup);
    return { data: items, page: 1, limit: items.length, total: items.length, totalPages: 1 };
  }

  const arr = Array.isArray(data?.data) ? data.data : Array.isArray(data?.groups) ? data.groups : [];
  const adapted = arr.map(adaptGroup);
  return {
    data: adapted,
    page: Number(data?.page ?? page ?? 1),
    limit: Number(data?.pageSize ?? pageSize ?? adapted.length),
    total: Number(data?.total ?? data?.totalCount ?? adapted.length),
    totalPages: Number(data?.totalPages ?? 1),
  };
}

export async function createCustomersRes(body: any) {
  return api.post("customers", body);
}

export async function updateCustomersRes(body: any) {
  return api.put("customers", body);
}


export async function getCustomerByIdRes(id: number | string) {
  return api.get("customers?id=" + id);
}


// ****************** CREATE CUSTOMER GROUP ********************
export async function createCustomerGroups(body: any) {
  return api.post("customergroup", body);
}

export async function updateCustomerGroups(body: any) {
  return api.put("customergroup/" + body.id, body);
}
